import { useState } from "react"
import { bool, number, shape, string } from "prop-types"
import { apiAsync as api } from "utils/api"
import pcoURL from "@planningcenter/url"
import useFeature from "hooks/use_feature"
import { RadioButton } from "components/ui"
import { SettingPane, useSettings, SettingContextProvider } from "./SettingPane"

const PaymentSettings = ({ event: eventFromProps, isStripeEnabled }) => {
  const { isEditing, onCancel, onSaving, onError, onSuccess } = useSettings()
  const { enabled: unifiedSignupsEnabled } = useFeature(
    "unified_detailed_signups_ccw",
    eventFromProps.id
  )

  const [event, setEvent] = useState(eventFromProps)
  const [paymentMethod, setPaymentMethod] = useState(eventFromProps.paymentMethod)

  const handleCancel = () => {
    setPaymentMethod(event.paymentMethod)
    onCancel()
  }

  const handleSave = async () => {
    onSaving(true)

    let paymentMethodAttrs = {
      credit_card_payments_enabled_at: new Date(),
      offline_payments_enabled_at: new Date()
    }

    if (paymentMethod === "credit") {
      paymentMethodAttrs = {
        ...paymentMethodAttrs,
        offline_payments_enabled_at: null
      }
    } else if (paymentMethod === "offline") {
      paymentMethodAttrs = {
        ...paymentMethodAttrs,
        credit_card_payments_enabled_at: null
      }
    }

    const response = await api.put(event.paths.show, { event: paymentMethodAttrs })

    if (response.errors) {
      onError(response.errors)
    } else {
      setEvent(response.event)
      await onSuccess()
    }
  }

  const handleChange = ({ target: { value } }) => setPaymentMethod(value)

  if (!unifiedSignupsEnabled) return null

  return (
    <SettingPane
      title="Payments"
      onSave={handleSave}
      onCancel={handleCancel}
      blankState={
        event.isArchived && (
          <div>This signup is archived so it can not be registered for on Church Center.</div>
        )
      }
    >
      {!isEditing && (
        <>
          <h4 className="label">Payment methods</h4>
          {event.paymentMethod === "credit" && "Credit/debit only"}
          {event.paymentMethod === "offline" && "Cash/check only"}
          {event.paymentMethod === "both" && "Both"}
        </>
      )}

      {isEditing && (
        <>
          <h4 className="label">Available payment methods</h4>
          <RadioButton
            name="paymentMethods"
            text="Credit/debit only"
            htmlFor="credit"
            value="credit"
            disabled={!isStripeEnabled}
            checked={paymentMethod === "credit"}
            onSelect={handleChange}
            helperMessage={
              !isStripeEnabled ? (
                <p>
                  If you want to accept credit/debit payments, you need to set up the Stripe
                  integration for your account.{" "}
                  <a
                    href={`${pcoURL(railsEnv)(
                      "accounts"
                    )}/stripe/oauth/new?from_app=Registrations&path=${window.location.pathname}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Set up now
                  </a>
                </p>
              ) : (
                ""
              )
            }
          />

          <RadioButton
            name="paymentMethods"
            text="Cash/check only"
            htmlFor="offline"
            value="offline"
            disabled={!isStripeEnabled}
            checked={paymentMethod === "offline"}
            onSelect={handleChange}
          />

          <RadioButton
            name="paymentMethods"
            text="Both"
            htmlFor="both"
            value="both"
            disabled={!isStripeEnabled}
            checked={paymentMethod === "both"}
            onSelect={handleChange}
          />

          {event.guestMode !== "disabled" && (
            <div className="font-size-4 mt-1 c-neutral-600">
              Require login must be enabled to accept online payments.
            </div>
          )}
        </>
      )}
    </SettingPane>
  )
}

PaymentSettings.propTypes = {
  isStripeEnabled: bool,
  event: shape({
    id: number,
    isSimple: bool,
    isArchived: bool,
    paymentMethod: string,
    paths: shape({
      show: string
    })
  })
}

const Payments = props => (
  <SettingContextProvider>
    <PaymentSettings {...props} />
  </SettingContextProvider>
)

export default Payments
